import { DOCUMENT } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';

import { AppConstant } from '@d-constants/app.constants';
import { environment } from '@d-environments/environment';
import { CheckIdleService } from '@d-services';
import { AuditService } from '@d-services/wms/audit.service';
import { AppState } from '@d-store/constant';
import { Store } from '@ngrx/store';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';

@Injectable()
export class SSOService {
  private document: Document;
  protected sessionCheckEventListener: any;

  constructor(
    private oauthService: OAuthService,
    protected checkIdleService: CheckIdleService,
    @Inject(DOCUMENT) document: any,
    protected ngZone: NgZone,
    protected store: Store<AppState>,
  ) {
    this.document = document;
    // this.configureOauthService();
  }

  private configureOauthService() {
    const authConfig = this.initializeAuthConfig();
    this.oauthService.configure(authConfig);
    /** enable below validation only if jwks object is defined as part of oauthconfig obj */
    // this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    this.oauthService.setStorage(localStorage);

    /** commented below because below resource is protected by some identity server ex: wso2 */
    //this.oauthService.loadDiscoveryDocumentAndTryLogin();
    this.oauthService.setupAutomaticSilentRefresh();

    this.initSessionCheck();
    this.oauthService.tryLogin().then(() => {
      this.initSessionCheck();
    });
  }

  private initializeAuthConfig(): AuthConfig {
    return {
      issuer: environment.sso.serverUrl.concat(environment.sso.issuer),
      redirectUri: environment.sso.redirectUri,
      clientId: environment.sso.clientId,
      dummyClientSecret: environment.sso.clientSecret,
      scope: environment.sso.scope,
      tokenEndpoint: environment.sso.serverUrl.concat(
        environment.sso.tokenEndpoint,
      ),
      userinfoEndpoint: environment.sso.serverUrl.concat(
        environment.sso.userinfoEndpoint,
      ),
      showDebugInformation: environment.sso.showDebugInformation,
      loginUrl: environment.sso.serverUrl.concat(
        environment.sso.authorizationEndpoint,
      ),
      requireHttps: environment.sso.requireHttps,
      disableAtHashCheck: true,
      responseType: environment.sso.responseType,
      logoutUrl: environment.sso.logoutUrl,
      postLogoutRedirectUri: environment.sso.postLogoutRedirectUri,
      useHttpBasicAuth: environment.sso.useHttpBasicAuth,
    };
  }

  public obtainAccessToken() {
    this.oauthService.initCodeFlow();
  }

  public getUserName(): string {
    const accessToken = this.getAccessToken();

    const claims = this.getUserClaims();
    //this.getUserInfo();
    return ''; //claims['sub'].split('@')[0];
  }

  public getUserInfo(): string {
    const idToken = this.oauthService.getIdToken();
    return typeof idToken['sub'] !== 'undefined'
      ? idToken['sub'].toString()
      : '';
  }

  public getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  public getCurrencySetting(): string {
    const currency: any = localStorage.getItem(
      AppConstant.LocalStorageKey.DIALOGTenantCurrency,
    );
    if (currency && JSON.parse(currency).symbol) {
      return JSON.parse(currency).symbol;
    }
    return '';
  }

  public getTenantId(): string {
    const curentUserInfo: any = localStorage.getItem(
      AppConstant.LocalStorageKey.DIALOGUser,
    );
    if (curentUserInfo && JSON.parse(curentUserInfo).user) {
      return JSON.parse(curentUserInfo).user['tenantId'];
    }
    return '';
  }
  public getCompanyId(): string {
    const curentUserInfo: any = localStorage.getItem(
      AppConstant.LocalStorageKey.DIALOGUser,
    );
    if (curentUserInfo && JSON.parse(curentUserInfo).user) {
      return JSON.parse(curentUserInfo).user['companyId'];
    }
    return '';
  }

  public getCountryId(): string {
    const curentUserInfo: any = localStorage.getItem(
      AppConstant.LocalStorageKey.DIALOGUser,
    );
    if (curentUserInfo) {
      let tenantAdmin: any[] =
        JSON.parse(curentUserInfo).currentTenant?.['listTenantAdmins'] ?? [];
      return tenantAdmin.length > 0 ? tenantAdmin[0]?.countryId : '';
    }
    return '';
  }

  public getUserClaims(): object {
    return this.oauthService.getIdentityClaims();
  }

  public isLoggedIn(): boolean {
    const accessToken = this.oauthService.getAccessToken();
    if (accessToken !== null) return true;
    else return false;
  }

  public logout(): void {
    const params = new HttpParams({
      fromObject: {
        post_logout_redirect_uri: environment.sso.postLogoutRedirectUri,
        id_token_hint: this.oauthService.getIdToken(),
      },
    });
    const logoutUrl =
      environment.sso.logoutUrl +
      `${environment.sso.logoutUrl.indexOf('?') > -1 ? '&' : '?'}` +
      params.toString();

    window.location.href = logoutUrl;

    localStorage.clear();
    sessionStorage.clear();
    document.cookie = '__cfduid=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    // this.oauthService.logOut(false);
  }

  //#region Session Check

  protected getSessionState(): string {
    return this.oauthService['getSessionState']();
  }

  protected canPerformSessionCheck(): boolean {
    if (!SessionConst.sessionChecksEnabled) {
      return false;
    }

    const sessionState = this.getSessionState();
    if (!sessionState) {
      return false;
    }

    if (typeof this.document === 'undefined') {
      return false;
    }

    return true;
  }

  protected initSessionCheck(): void {
    // Check user Idle
    this.checkIdleService.startTimer();
    if (!this.canPerformSessionCheck()) {
      return;
    }

    const existingIframe = this.document.getElementById(
      SessionConst.sessionCheckIFrameName,
    );
    if (existingIframe) {
      this.document.body.removeChild(existingIframe);
    }

    const iframe = this.document.createElement('iframe');
    iframe.id = SessionConst.sessionCheckIFrameName;

    this.setupSessionCheckEventListener();

    const url = SessionConst.sessionCheckIFrameUrl;
    // iframe.setAttribute('src', url); // This line cause reload
    iframe.style.display = 'none';
    this.document.body.appendChild(iframe);

    this.startSessionCheckTimer();
  }

  protected startSessionCheckTimer(): void {
    this.stopSessionCheckTimer();
    this.ngZone.runOutsideAngular(() => {
      //   SessionConst.sessionCheckTimer = setInterval(
      //     this.checkSession.bind(this),
      //     SessionConst.sessionCheckIntervall
      //   );
    });
  }

  protected stopSessionCheckTimer(): void {
    if (SessionConst.sessionCheckTimer) {
      clearInterval(SessionConst.sessionCheckTimer);
      SessionConst.sessionCheckTimer = null;
    }
  }

  public checkSession(): void {
    const iframe: any = this.document.getElementById(
      SessionConst.sessionCheckIFrameName,
    );

    if (!iframe) {
      console.warn(
        'checkSession did not find iframe',
        SessionConst.sessionCheckIFrameName,
      );
    }

    const sessionState = this.getSessionState();

    if (!sessionState) {
      this.stopSessionCheckTimer();
    }

    const message = this.oauthService.clientId + ' ' + sessionState;
    iframe.contentWindow.postMessage(message, this.oauthService.issuer);
  }

  protected setupSessionCheckEventListener(): void {
    this.removeSessionCheckEventListener();

    this.sessionCheckEventListener = (e: MessageEvent) => {
      const origin = e.origin.toLowerCase();
      const issuer = this.oauthService.issuer?.toLowerCase();

      if (!issuer?.startsWith(origin)) {
        return;
      }

      // only run in Angular zone if it is 'changed' or 'error'
      switch (e.data) {
        case 'changed':
          this.ngZone.run(() => {
            this.handleSessionChange();
          });
          break;

        case 'logout':
        case 'error':
          this.ngZone.run(() => {
            this.handleSessionLogout();
          });
          break;
      }
    };

    // prevent Angular from refreshing the view on every message (runs in intervals)
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('message', this.sessionCheckEventListener);
    });
  }

  protected removeSessionCheckEventListener(): void {
    if (this.sessionCheckEventListener) {
      window.removeEventListener('message', this.sessionCheckEventListener);
      this.sessionCheckEventListener = null;
    }
  }

  protected handleSessionChange(): void {
    this.stopSessionCheckTimer();
    localStorage.clear();
    sessionStorage.clear();
    document.cookie = '__cfduid=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    this.oauthService.initImplicitFlow();
  }

  protected handleSessionLogout(): void {
    this.stopSessionCheckTimer();
    this.logout();
  }

  //#endregion
}

export const SessionConst = {
  sessionCheckTimer: null,
  sessionChecksEnabled: true,
  sessionCheckIFrameName: 'angular-oauth-oidc-check-session-iframe',
  sessionCheckIFrameUrl:
    environment.sso.serverUrl +
    environment.sso.sessionCheckIFrameUrl +
    '?client_id=' +
    environment.sso.clientId,
  sessionCheckIntervall: 3000,
  getSessionCheckTimer: function () {
    if (!SessionConst.sessionCheckTimer) {
      return null;
    }

    return SessionConst.sessionCheckTimer;
  },
};
